type StringTransformer = (str: string) => string;
export default function useKeyConversion (resultConvention = 'camel') {
  function convertKeys (obj: any, sourceConvention: string | null = null): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => convertKeys(item, sourceConvention));
    }

    sourceConvention ||= detectConvention(Object.keys(obj));

    const conversionFunc = getConversionFunction(sourceConvention);
    if (conversionFunc === 'origin') {
      return obj;
    }
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [conversionFunctions[conversionFunc](key), convertKeys(value, sourceConvention)])
    );
  }

  function detectConvention (keys: string[]): string {
    const snakeCaseRegex = /^([a-zA-Z]+_[a-zA-Z]+)+$/;
    const kebabCaseRegex = /^([a-zA-Z]+-[a-zA-Z]+)+$/;
    const pascalCaseRegex = /^[A-Z][a-zA-Z]+$/;
    const camelCaseRegex = /^[a-z][a-zA-Z]+$/;

    const hasSnakeCase = keys.some(key => snakeCaseRegex.test(key));
    const hasKebabCase = keys.some(key => kebabCaseRegex.test(key));
    const hasPascalCase = keys.every(key => pascalCaseRegex.test(key));
    const hasCamelCase = keys.every(key => camelCaseRegex.test(key));

    if (hasSnakeCase) {
      return 'snake';
    } else if (hasKebabCase) {
      return 'kebab';
    } else if (hasPascalCase) {
      return 'pascal';
    } else if (hasCamelCase) {
      return 'camel';
    } else {
      return 'unknown';
    }
  }

  function getConversionFunction (sourceConvention: string): string {
    return sourceConvention !== resultConvention ? `${sourceConvention}To${resultConvention?.charAt(0).toUpperCase() + resultConvention.slice(1)}` : 'origin';
  }

  const conversionFunctions: Record<string, StringTransformer> = {
    camelToSnake: (str: string) => str.replace(/[A-Z]/g, match => '_' + match.toLowerCase()),
    camelToKebab: (str: string) => str.replace(/[A-Z]/g, match => '-' + match.toLowerCase()),
    camelToPascal: (str: string) => str.charAt(0).toUpperCase() + str.slice(1),

    snakeToPascal: (str: string) => str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(''),
    snakeToKebab: (str: string) => str.replace(/_/g, '-'),
    snakeToCamel: (str: string) => str.replace(/_./g, match => match.charAt(1).toUpperCase()),

    pascalToKebab: (str: string) => str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase(),
    pascalToSnake: (str: string) => str.replace(/[A-Z]/g, match => '_' + match.toLowerCase()),
    pascalToCamel: (str: string) => str.charAt(0).toLowerCase() + str.slice(1),

    kebabToSnake: (str: string) => str.replace(/-/g, '_'),
    kebabToPascal: (str: string) => str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(''),
    kebabToCamel: (str: string) => str.replace(/-./g, match => match.charAt(1).toUpperCase())
  };

  return {
    convertKeys
  };
}
